import type { FC, ReactNode } from 'react'
import type { BrandSummary, PageSection } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cms } from '@/lib/cms'

interface BrandCardProps {
    brand: BrandSummary
    cta?: PageSection | null
}

const BrandCard: FC<BrandCardProps> = ({ brand, cta }): ReactNode => {
    return (
        <Link
            href={`/brands/${brand.slug}`}
            className='group block h-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
        >
            <article className='flex h-full flex-col'>
                <div className='relative aspect-[4/5] overflow-hidden bg-sand'>
                    {brand.cover_image
                        ? (
                            <img
                                src={brand.cover_image}
                                alt={brand.cover_image_alt ?? ''}
                                loading='lazy'
                                className='size-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]'
                            />
                        )
                        : (
                            <div aria-hidden='true' className='flex size-full items-center justify-center p-8'>
                                <span data-mdlfy-cms={cms('brands', brand.id, 'name')} className='display text-center text-3xl text-foreground/30'>{brand.name}</span>
                            </div>
                        )}

                    {brand.logo && (
                        <div className='absolute bottom-4 left-4 bg-background/95 px-4 py-3'>
                            <img src={brand.logo} alt={brand.logo_alt ?? brand.name} loading='lazy' className='h-7 w-auto max-w-36 object-contain' />
                        </div>
                    )}
                </div>

                <div className='flex flex-1 flex-col pt-6'>
                    {brand.category && (
                        <p className='eyebrow text-muted-foreground'>
                            <span data-mdlfy-cms={cms('brands', brand.id, 'category')}>{brand.category}</span>
                        </p>
                    )}

                    <h3 className='display mt-3 text-2xl'>
                        <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                    </h3>

                    {brand.origin && (
                        <p className='mt-2 text-sm text-clay'>
                            <span data-mdlfy-cms={cms('brands', brand.id, 'origin')}>{brand.origin}</span>
                        </p>
                    )}

                    {brand.summary && (
                        <p className='mt-4 text-[0.95rem] leading-relaxed text-foreground/75'>
                            <span data-mdlfy-cms={cms('brands', brand.id, 'summary')}>{brand.summary}</span>
                        </p>
                    )}

                    <span className='mt-auto inline-flex items-center gap-2 pt-6 text-foreground'>
                        {cta?.button_label
                            ? <span className='eyebrow' data-mdlfy-cms={cms('page_sections', cta.id, 'button_label')}>{cta.button_label}</span>
                            : <span className='eyebrow'>Discover</span>}
                        <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' aria-hidden='true' />
                    </span>
                </div>
            </article>
        </Link>
    )
}

export default BrandCard
